import React from "react";
import { Table } from "reactstrap";
import PropTypes from "prop-types";

const countUserRows = (categories = []) =>
    (Array.isArray(categories) ? categories : []).reduce((sum, category) => {
        const jobCount = Array.isArray(category?.jobs) ? category.jobs.length : 0;
        return sum + jobCount + 1;
    }, 1);

const renderClosureCountCell = (value, isClickable = false, onClick) => {
    const displayValue = value ?? 0;
    if (!isClickable) {
        return displayValue;
    }

    return (
        <button
            type="button"
            className="btn btn-link p-0 border-0 closure-report-count-link"
            onClick={onClick}
        >
            {displayValue}
        </button>
    );
};

const ClosureReportTable = ({ data = [], onClosureCountClick }) => {
    const userGroups = Array.isArray(data) ? data : [];

    if (!userGroups.length) {
        return (
            <div className="closure-report-table closure-report-table--empty text-center text-muted py-4">
                No data found
            </div>
        );
    }

    return (
        <div className="table-responsive closure-report-table">
            <Table bordered className="align-middle mb-0 closure-report-table-inner">
                <thead className="table-light">
                    <tr>
                        <th>Name</th>
                        <th>Job Category</th>
                        <th>Job</th>
                        <th className="text-end">Closure Count</th>
                    </tr>
                </thead>

                <tbody>
                    {userGroups.map((userGroup = {}, userIndex) => {
                        const categories = Array.isArray(userGroup?.categories)
                            ? userGroup.categories
                            : [];
                        const userRowSpan = countUserRows(categories);
                        let isFirstUserRow = true;

                        const categoryRows = categories.flatMap((category = {}, categoryIndex) => {
                            const jobs = Array.isArray(category?.jobs) ? category.jobs : [];
                            const categoryRowSpan = jobs.length + 1;
                            const rows = [];

                            jobs.forEach((job = {}, jobIndex) => {
                                rows.push(
                                    <tr key={`${userIndex}-${categoryIndex}-job-${jobIndex}`}>
                                        {isFirstUserRow ? (
                                            <td
                                                rowSpan={userRowSpan}
                                                className="closure-report-name-cell"
                                            >
                                                {userGroup?.name || ""}
                                            </td>
                                        ) : null}
                                        {jobIndex === 0 ? (
                                            <td rowSpan={categoryRowSpan}>
                                                {category?.jobCategory || ""}
                                            </td>
                                        ) : null}
                                        <td>{job?.job || ""}</td>
                                        <td className="closure-report-count-cell">
                                            {renderClosureCountCell(
                                                job?.closure,
                                                job?.isClickable,
                                                () =>
                                                    onClosureCountClick?.({
                                                        userName: userGroup?.name || "",
                                                        jobCategoryName: category?.jobCategory || "",
                                                        jobName: job?.job || "",
                                                        closureCount: job?.closure ?? 0,
                                                        candidates: job?.candidates ?? [],
                                                    })
                                            )}
                                        </td>
                                    </tr>
                                );
                                isFirstUserRow = false;
                            });

                            rows.push(
                                <tr
                                    key={`${userIndex}-${categoryIndex}-total`}
                                    className="closure-report-sub-total-row"
                                >
                                    <td>Total</td>
                                    <td className="closure-report-count-cell">
                                        {category?.total ?? 0}
                                    </td>
                                </tr>
                            );

                            return rows;
                        });

                        return (
                            <React.Fragment key={userGroup?.id || userGroup?.name || userIndex}>
                                {categoryRows}
                                <tr className="closure-report-user-total-row">
                                    <td colSpan={2}>Total</td>
                                    <td className="closure-report-count-cell">
                                        {userGroup?.grandTotal ?? 0}
                                    </td>
                                </tr>
                                {userIndex < userGroups.length - 1 ? (
                                    <tr className="closure-report-section-gap" aria-hidden="true">
                                        <td colSpan={4} />
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
};

ClosureReportTable.propTypes = {
    data: PropTypes.array,
    onClosureCountClick: PropTypes.func,
};

export default ClosureReportTable;
